import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useUserRole } from "@/hooks/useUserRole";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuBadge,
  SidebarHeader,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  BarChart3,
  ShoppingCart,
  UserCheck,
  Users,
  Stethoscope,
  Building2,
  UserPlus,
  Briefcase,
  DollarSign,
  Target,
  Mail,
  MessageSquare,
  ClipboardCheck,
  ScrollText,
  Activity,
  Zap,
  ShieldAlert,
  Timer,
  Wrench,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface AdminSidebarProps {
  activeTab: string;
  onNavigateToTab: (tab: string) => void;
}

interface BadgeCounts {
  pendingOrders: number;
  pendingAssessments: number;
  pendingInvites: number;
  pendingCredentials: number;
  openQA: number;
  openErrors: number;
  totalActions: number;
}

const navGroups = [
  {
    label: "Core",
    items: [
      { id: "overview", label: "Overview", icon: BarChart3, countKey: "totalActions" as const },
      { id: "orders", label: "Orders", icon: ShoppingCart, countKey: "pendingOrders" as const },
      { id: "patients", label: "Patients", icon: UserCheck, countKey: "pendingAssessments" as const },
    ],
  },
  {
    label: "People",
    items: [
      { id: "users", label: "Users & Roles", icon: Users },
      { id: "providers", label: "Providers", icon: Stethoscope, countKey: "pendingCredentials" as const },
      { id: "pharmacies", label: "Pharmacies", icon: Building2 },
      { id: "invites", label: "Invites", icon: UserPlus, countKey: "pendingInvites" as const },
    ],
  },
  {
    label: "Revenue",
    items: [
      { id: "sales", label: "Sales", icon: Briefcase },
      { id: "payouts", label: "Payouts", icon: DollarSign, superAdminOnly: true },
      { id: "cac-ltv", label: "CAC / LTV", icon: Target },
    ],
  },
  {
    label: "Operations",
    items: [
      { id: "emails", label: "Emails", icon: Mail },
      { id: "messages", label: "Messages", icon: MessageSquare },
      { id: "qa", label: "QA", icon: ClipboardCheck, countKey: "openQA" as const },
    ],
  },
  {
    label: "System",
    items: [
      { id: "audit", label: "Audit Logs", icon: ScrollText, superAdminOnly: true },
      { id: "errors", label: "User Errors", icon: ShieldAlert, countKey: "openErrors" as const },
      { id: "cron", label: "Cron Jobs", icon: Timer, superAdminOnly: true },
      { id: "operations", label: "Operations", icon: Wrench, superAdminOnly: true },
      { id: "security", label: "Security", icon: Activity, superAdminOnly: true },
    ],
  },
];

const AdminSidebar = ({ activeTab, onNavigateToTab }: AdminSidebarProps) => {
  const { state } = useSidebar();
  const collapsed = state === "collapsed";
  const { isSuperAdmin } = useUserRole();
  const [counts, setCounts] = useState<BadgeCounts>({
    pendingOrders: 0,
    pendingAssessments: 0,
    pendingInvites: 0,
    pendingCredentials: 0,
    openQA: 0,
    openErrors: 0,
    totalActions: 0,
  });

  useEffect(() => {
    const fetchCounts = async () => {
      const [orders, assessments, invites, credentials, qa, errors] = await Promise.all([
        supabase.from("orders").select("id", { count: "exact", head: true }).eq("status", "pending"),
        supabase.from("health_assessments").select("id", { count: "exact", head: true }).eq("status", "pending"),
        supabase.from("role_requests").select("id", { count: "exact", head: true }).eq("status", "pending"),
        supabase.from("provider_credentials").select("id", { count: "exact", head: true }).in("credential_status", ["pending_review", "expired"]),
        supabase.from("qa_issues").select("id", { count: "exact", head: true }).eq("status", "open"),
        supabase.from("admin_error_log").select("id", { count: "exact", head: true }).is("resolved_at", null),
      ]);
      const po = orders.count || 0;
      const pa = assessments.count || 0;
      const pi = invites.count || 0;
      const pc = credentials.count || 0;
      const oq = qa.count || 0;
      const oe = errors.count || 0;
      setCounts({
        pendingOrders: po,
        pendingAssessments: pa,
        pendingInvites: pi,
        pendingCredentials: pc,
        openQA: oq,
        openErrors: oe,
        totalActions: po + pa + pi + pc + oq,
      });
    };
    fetchCounts();
    const interval = setInterval(fetchCounts, 60000);
    return () => clearInterval(interval);
  }, []);

  const totalPending = Object.values(counts).reduce((a, b) => a + b, 0);

  return (
    <Sidebar collapsible="icon" className="border-r">
      <SidebarHeader className="p-3">
        <button
          onClick={() => onNavigateToTab("overview")}
          className={`w-full flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
            activeTab === "overview"
              ? "bg-primary/10 text-primary"
              : "hover:bg-muted text-muted-foreground"
          }`}
        >
          <Zap size={16} className="shrink-0 text-amber-500" />
          {!collapsed && (
            <>
              <span>Action Queue</span>
              {isSuperAdmin && (
                <Badge variant="outline" className="ml-1 text-[9px] uppercase tracking-wide border-amber-500/50 text-amber-600 dark:text-amber-400">
                  Super
                </Badge>
              )}
              {totalPending > 0 && (
                <Badge variant="destructive" className="ml-auto text-[10px] h-5 min-w-5 flex items-center justify-center">
                  {totalPending}
                </Badge>
              )}
            </>
          )}
        </button>
      </SidebarHeader>
      <SidebarContent>
        {navGroups.map((group) => (
          <SidebarGroup key={group.label}>
            <SidebarGroupLabel className="text-[10px] uppercase tracking-wider text-muted-foreground/70 font-semibold">
              {group.label}
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {group.items
                  .filter((item) => !(item as { superAdminOnly?: boolean }).superAdminOnly || isSuperAdmin)
                  .map((item) => {
                  const countKey = (item as { countKey?: keyof BadgeCounts }).countKey;
                  const count = countKey ? counts[countKey] : 0;
                  const isActive = activeTab === item.id;
                  return (
                    <SidebarMenuItem key={item.id}>
                      <SidebarMenuButton
                        onClick={() => onNavigateToTab(item.id)}
                        isActive={isActive}
                        tooltip={item.label}
                        className="cursor-pointer"
                      >
                        <item.icon size={16} />
                        <span>{item.label}</span>
                      </SidebarMenuButton>
                      {count > 0 && !collapsed && (
                        <SidebarMenuBadge className="bg-destructive/10 text-destructive text-[10px] font-semibold">
                          {count}
                        </SidebarMenuBadge>
                      )}
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>
    </Sidebar>
  );
};

export default AdminSidebar;
